<script setup lang="ts">
/**
 * CampaignTabContent — onglet "À propos" : vidéo YouTube + description
 * (repliable) + CTAs inline (Je participe / Partager) + liste des mises à jour.
 *
 * Composant PARTAGÉ (@donoor/ui) consommé en SOURCE par dashboard.donoor.org
 * et donoor.org. Couleurs en hex en dur (pas de token de thème) → rendu
 * identique dans les 2 apps malgré leurs thèmes différents.
 *
 * Le composant ne connaît PAS la destination du don : il émet `donate` et
 * chaque page décide (public → naviguer, preview → simuler). Idem `openShare`.
 *
 * `<UIcon>` reste global (résolu par l'app Nuxt qui transpile ce SFC via
 * build.transpile). `useSanitizeHtml()` est un auto-import présent dans les
 * 2 apps (signature identique, retourne `{ sanitize }`).
 *
 * USAGE (auto-import via wrapper local) :
 *   import CampaignTabContent from "@donoor/ui/campaign/CampaignTabContent.vue";
 *
 * Karim 2026-06-21 (factorisation cagnotte).
 */
import type { Campaign } from "./types";

// Internationalisation (lot "paquets partagés", 2026-10-11) : clés `ui.*`
// et `commun.*` (bouton Partager), présentes dans donoor.org ET dashboard.
const { t } = useI18n();

const props = defineProps<{
  campaign: Campaign;
  hasUpdates: boolean;
  youtubeEmbedUrl: string | null;
  formatUpdateDate: (dateString: string) => string;
  isCampaignClosed?: boolean;
  isCampaignPaused?: boolean;
}>();

const emit = defineEmits<{
  openShare: [];
  donate: [];
}>();

const { sanitize } = useSanitizeHtml();

/**
 * Mises à jour, la plus RÉCENTE en haut (même ordre que la liste du
 * dashboard — Karim 2026-09-30) ; une actualité sans titre ni texte n'est
 * pas affichée (brouillon vide enregistré par mégarde).
 */
const actualites = computed(() =>
  [...(props.campaign?.campaign_updates ?? [])]
    .filter((u) => (u.title ?? "").trim() || (u.content ?? "").replace(/<[^>]*>/g, "").trim())
    .sort((a, b) => (Date.parse(b.date_posted) || 0) - (Date.parse(a.date_posted) || 0)),
);

const isDescriptionExpanded = ref(false);
const descriptionRef = ref<HTMLElement | null>(null);
const needsExpansion = ref(true);
const fullHeight = ref(0);

const checkDescriptionHeight = () => {
  if (descriptionRef.value) {
    fullHeight.value = descriptionRef.value.scrollHeight;
    needsExpansion.value = descriptionRef.value.scrollHeight > 280;
  }
};

onMounted(() => {
  requestAnimationFrame(() => {
    checkDescriptionHeight();
  });
});

watch(
  () => props.campaign?.description,
  () => {
    isDescriptionExpanded.value = false;
    needsExpansion.value = true;
    nextTick(() => {
      requestAnimationFrame(() => {
        checkDescriptionHeight();
      });
    });
  },
);

const toggleDescription = () => {
  isDescriptionExpanded.value = !isDescriptionExpanded.value;
};
</script>

<template>
  <div class="space-y-8">
    <!-- Vidéo -->
    <div
      v-if="youtubeEmbedUrl"
      class="relative rounded-3xl overflow-hidden bg-black aspect-video"
    >
      <iframe
        :src="youtubeEmbedUrl"
        class="w-full h-full"
        frameborder="0"
        allow="
          accelerometer;
          autoplay;
          clipboard-write;
          encrypted-media;
          gyroscope;
          picture-in-picture;
        "
        allowfullscreen
      />
    </div>

    <hr
      v-if="youtubeEmbedUrl && campaign.description"
      class="border-t border-gray-300"
    />

    <!-- Description -->
    <div v-if="campaign.description">
      <div class="relative">
        <div
          class="overflow-hidden transition-[max-height] duration-500 ease-in-out"
          :style="{ maxHeight: isDescriptionExpanded ? `${fullHeight}px` : '280px' }"
        >
          <!-- eslint-disable vue/no-v-html -->
          <!-- Reason: contenu déjà sanitizé via sanitize() (DOMPurify) -->
          <div
            ref="descriptionRef"
            class="prose prose-lg prose-gray dark:prose-invert max-w-none prose-headings:font-bold prose-headings:text-gray-900 dark:prose-headings:text-white prose-p:text-gray-900! dark:prose-p:text-gray-100 prose-strong:text-gray-900 dark:prose-strong:text-white prose-ul:list-disc prose-ul:pl-6 prose-li:text-gray-900! dark:prose-li:text-gray-100 campaign-description"
            v-html="sanitize(campaign.description)"
          />
          <!-- eslint-enable vue/no-v-html -->
        </div>
        <div
          class="absolute bottom-0 left-0 right-0 h-20 bg-linear-to-t from-gray-50 to-transparent pointer-events-none transition-opacity duration-300"
          :class="needsExpansion && !isDescriptionExpanded ? 'opacity-100' : 'opacity-0'"
        />
      </div>
      <button
        v-if="needsExpansion"
        type="button"
        class="mt-3 flex items-center gap-2 text-[#0F3D8C] font-bold text-base hover:text-[#0A2D6B] transition-colors"
        @click="toggleDescription"
      >
        <UIcon
          :name="
            isDescriptionExpanded
              ? 'i-lucide-chevron-up'
              : 'i-lucide-chevron-down'
          "
          class="size-4 transition-transform"
        />
        {{ isDescriptionExpanded ? t("ui.tabContent.afficherMoins") : t("ui.tabContent.afficherPlus") }}
      </button>

      <!-- CTA inline (boost participation / partage) -->
      <div
        v-if="!isCampaignClosed && !isCampaignPaused"
        class="mt-8 grid grid-cols-2 gap-3"
      >
        <button
          type="button"
          class="py-3 rounded-full text-base font-bold bg-white text-gray-900 border border-gray-300 hover:bg-gray-50 transition-colors"
          @click="emit('donate')"
        >
          {{ t("ui.commun.jeParticipe") }}
        </button>
        <button
          type="button"
          class="py-3 rounded-full text-base font-bold bg-white text-gray-900 border border-gray-300 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
          @click="emit('openShare')"
        >
          <UIcon name="i-lucide-share-2" class="size-4" />
          {{ t("commun.actions.partager") }}
        </button>
      </div>
    </div>
    <div
      v-else-if="!youtubeEmbedUrl"
      class="text-center py-8 text-gray-500 dark:text-gray-400"
    >
      <UIcon name="i-lucide-file-text" class="size-12 mb-3 opacity-50" />
      <p>{{ t("ui.tabContent.aucuneDescription") }}</p>
    </div>

    <hr v-if="hasUpdates" class="border-t border-gray-200" />

    <!-- Mises à jour -->
    <section v-if="hasUpdates" class="pt-2">
      <div class="flex items-center gap-3 mb-6">
        <h2 class="text-2xl lg:text-4xl font-bold text-gray-900 dark:text-white">
          {{ t("ui.tabContent.misesAJour") }}
        </h2>
        <span
          class="inline-flex items-center justify-center min-w-7 h-7 px-2 text-sm font-bold rounded-full bg-[#D7EBFF] text-[#0F3D8C]"
        >
          {{ actualites.length }}
        </span>
      </div>
      <div class="space-y-8">
        <div
          v-for="(update, index) in actualites"
          :key="index"
          class="border-l-2 border-gray-200 dark:border-gray-700 pl-5"
        >
          <div
            class="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-3"
          >
            <UIcon name="i-lucide-calendar" class="size-4" />
            <span class="font-medium">{{
              formatUpdateDate(update.date_posted)
            }}</span>
          </div>
          <!-- Titre optionnel (2026-09-30) : ce qui permet de s'y retrouver. -->
          <h3
            v-if="(update.title ?? '').trim()"
            class="text-lg lg:text-xl font-bold text-gray-900 dark:text-white mb-2"
          >
            {{ update.title }}
          </h3>
          <!-- eslint-disable vue/no-v-html -->
          <!-- Reason: contenu déjà sanitizé via sanitize() (DOMPurify) -->
          <div
            class="prose prose-lg prose-gray dark:prose-invert max-w-none prose-headings:font-bold prose-headings:text-gray-900 dark:prose-headings:text-white prose-p:text-gray-900! dark:prose-p:text-gray-100 prose-strong:text-gray-900 dark:prose-strong:text-white prose-ul:list-disc prose-ul:pl-6 prose-li:text-gray-900! dark:prose-li:text-gray-100 campaign-description"
            v-html="sanitize(update.content)"
          />
          <!-- eslint-enable vue/no-v-html -->
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped>
.campaign-description :deep(p),
.campaign-description :deep(li),
.campaign-description :deep(span),
.campaign-description :deep(a),
.campaign-description :deep(strong),
.campaign-description :deep(em) {
  color: #111827 !important;
}
.campaign-description :deep(h1),
.campaign-description :deep(h2),
.campaign-description :deep(h3),
.campaign-description :deep(h4) {
  color: #111827 !important;
}
.campaign-description :deep(p:empty),
.campaign-description :deep(p:has(> br:only-child)) {
  min-height: 1.5em;
}
.campaign-description :deep(br) {
  display: block;
  content: "";
  margin-top: 0.5em;
}
.campaign-description :deep(p + p) {
  margin-top: 1em;
}
</style>
